import React, { useEffect, useState } from "react";
import { addSeconds, formatDistanceToNowStrict, isPast } from "date-fns";
import { REPORT_EXPORTS } from "@sscs/types";
import { Button, Dialog, Link, QueryResult } from "@sscs/ui";
import { DownloadIcon } from "@sscs/ui/icons";
import { capitalizeFirstLetter, getQueryStatus } from "@sscs/utils";
import { REPORT_EXPORT_FILE_NAME } from "Constants";
import { useDownloadRlSafeReportQuery } from "../../services/download.api";
import styles from "./rlSafeExportDialog.module.scss";

type LinkExpirationProps = {
  fetchedAt: string;
  expiresIn: number;
  onClose: () => void;
};

const LinkExpiration = ({
  fetchedAt,
  expiresIn,
  onClose,
}: LinkExpirationProps): React.JSX.Element => {
  const expirationDate = addSeconds(new Date(fetchedAt), expiresIn);
  const [hasExpired, setHasExpired] = useState(isPast(expirationDate));
  const [timeStatus, setTimeStatus] = useState(
    formatDistanceToNowStrict(expirationDate, { addSuffix: true }),
  );

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    interval = setInterval(() => {
      setTimeStatus(
        formatDistanceToNowStrict(expirationDate, { addSuffix: true }),
      );

      if (!hasExpired) {
        setHasExpired(isPast(expirationDate));
      }
    }, 5 * 1000);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [expirationDate, hasExpired, onClose]);

  return (
    <div className={styles.expires} title={expirationDate.toString()}>
      Link expire{hasExpired ? "d" : "s"}: {capitalizeFirstLetter(timeStatus)}
    </div>
  );
};

type RlSafeExportDialogProps = {
  isOpen: boolean;
  reportId: string;
  onClose: () => void;
};

const RlSafeExportDialogContent = ({
  reportId,
  onClose,
}: Omit<RlSafeExportDialogProps, "isOpen">): React.JSX.Element => {
  const { data, isFetching, isLoading, isError, error } =
    useDownloadRlSafeReportQuery({ report_id: reportId });

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          Download {REPORT_EXPORTS.safe} report
        </Dialog.Header.Title>
      </Dialog.Header>
      <Dialog.Body>
        <QueryResult
          status={getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
          })}
        >
          <div>
            <strong>{REPORT_EXPORT_FILE_NAME.safe}</strong>
            {data?.fetchedAt ? (
              <LinkExpiration
                fetchedAt={data.fetchedAt}
                expiresIn={data.expires}
                onClose={onClose}
              />
            ) : null}
          </div>
        </QueryResult>
        <Dialog.Banner type="info" title="SAFE Viewer required">
          The archive holds the complete SAFE report and every{" "}
          <Link
            id="rl-safe-viewer-doc-link"
            href="https://docs.secure.software/safe-viewer#export-report"
            target="_blank"
            rel="noreferrer"
            inline
          >
            other supported report
          </Link>
          . Opening it, and exporting the other reports from it, needs the SAFE
          Viewer application:{" "}
          <Link
            id="rl-safe-viewer-download-link"
            href="https://docs.secure.software/safe-viewer"
            target="_blank"
            rel="noreferrer"
            inline
          >
            Get SAFE Viewer
          </Link>
        </Dialog.Banner>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id="rl-safe-export"
            href={data?.download_link}
            startIcon={<DownloadIcon />}
            onClick={onClose}
            disabled={!data}
            tooltipTitle={data ? undefined : "Preparing the archive"}
          >
            Download
          </Button>
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

const RlSafeExportDialog = ({
  isOpen,
  ...props
}: RlSafeExportDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <RlSafeExportDialogContent {...props} />
  </Dialog.Frame>
);

export default RlSafeExportDialog;
